// Shared table primitives. These were copy-pasted (and had drifted) across
// every *Table.js; keep the single source of truth here.

import { Fragment } from 'react';

export const SortIcon = ({ col, sortConfig }) => {
  if (sortConfig.key !== col) {
    return (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.35, marginLeft: 4 }}>
        <path d="M12 5v14M5 12l7-7 7 7" />
      </svg>
    );
  }
  return sortConfig.direction === 'asc'
    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--coral)" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 19V5M5 12l7-7 7 7" /></svg>
    : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--coral)" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 5v14M5 12l7 7 7-7" /></svg>;
};

// Sortable column header with keyboard support and aria-sort.
export const SortableTh = ({ col, label, sortConfig, onSort, style }) => {
  const active = sortConfig.key === col;
  const ariaSort = active ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th
      aria-sort={ariaSort}
      style={{ cursor: 'pointer', userSelect: 'none', ...style }}
      tabIndex={0}
      onClick={() => onSort(col)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(col); }
      }}
    >
      {label} <SortIcon col={col} sortConfig={sortConfig} />
    </th>
  );
};

// Per-column filter cells for the second thead row (issue #144).
// The header cell resets the th's uppercase/letter-spacing so the control
// reads as an input, not a heading.
const FILTER_TH_STYLE = {
  padding: '0 10px 10px',
  cursor: 'default',
};

const FILTER_CONTROL_STYLE = {
  width: '100%',
  height: '28px',
  boxSizing: 'border-box',
  padding: '0 8px',
  fontFamily: 'var(--font-sans)',
  fontSize: '12.5px',
  fontWeight: 400,
  letterSpacing: 'normal',
  textTransform: 'none',
  color: 'var(--ink)',
  background: 'var(--canvas)',
  border: '1px solid var(--hairline)',
  borderRadius: '6px',
  outline: 'none',
};

// One cell of the filter row. Three shapes:
//   text input:  <FilterTh label="Name" name="fullName" value={...} onChange={...} />
//   select:      same plus options={['Active', 'Inactive']} or [{ value, label }]
//   empty cell:  <FilterTh /> (date/numeric/action columns)
// onChange receives the raw change event so tables can reuse their existing
// name-keyed filter handlers.
export const FilterTh = ({ label, name, value, onChange, options, allLabel = 'All', style }) => {
  if (!onChange) return <th aria-hidden="true" style={{ ...FILTER_TH_STYLE, ...style }} />;
  return (
    <th style={{ ...FILTER_TH_STYLE, ...style }}>
      {options ? (
        <select
          name={name}
          value={value}
          onChange={onChange}
          aria-label={`Filter by ${label}`}
          style={FILTER_CONTROL_STYLE}
        >
          <option value="">{allLabel}</option>
          {options.map(o => (typeof o === 'object'
            ? <option key={o.value} value={o.value}>{o.label}</option>
            : <option key={o} value={o}>{o}</option>))}
        </select>
      ) : (
        <input
          name={name}
          value={value}
          onChange={onChange}
          aria-label={`Filter by ${label}`}
          placeholder="Filter"
          style={FILTER_CONTROL_STYLE}
        />
      )}
    </th>
  );
};

// Header, filter and body rows rendered from a column descriptor list, so the
// user's column choice (issue #148) applies to all three at once instead of
// each table hardcoding its columns three times.
//
// A descriptor is:
//   key           column id, also the sort key unless `sortKey` says otherwise
//   label         header text
//   locked        always visible, never reordered
//   defaultHidden starts switched off
//   sortable      false for columns the backend cannot sort on
//   filter        { name, options, allLabel, value, onChange } or omitted
//   cell          (row) => <td>…</td>
export const ColumnHeaderRow = ({ columns, sortConfig, onSort }) => (
  <tr>
    {columns.map(col => (col.sortable === false
      ? <th key={col.key}>{col.label}</th>
      : <SortableTh
          key={col.key}
          col={col.sortKey || col.key}
          label={col.label}
          sortConfig={sortConfig}
          onSort={onSort}
        />))}
  </tr>
);

export const ColumnFilterRow = ({ columns, filter, onChange }) => (
  <tr>
    {columns.map(col => (col.filter
      ? <FilterTh
          key={col.key}
          label={col.label}
          name={col.filter.name}
          value={col.filter.value ? col.filter.value(filter) : filter[col.filter.name]}
          onChange={col.filter.onChange || onChange}
          options={col.filter.options}
          allLabel={col.filter.allLabel}
        />
      : <FilterTh key={col.key} />))}
  </tr>
);

// Each descriptor returns its own <td>, so a column keeps whatever markup it
// already had (links, badges, action menus) with no prop plumbing.
export const ColumnCells = ({ columns, row }) => (
  <>
    {columns.map(col => <Fragment key={col.key}>{col.cell(row)}</Fragment>)}
  </>
);

const BADGE_CLASS = {
  active: 'badge-success',
  approved: 'badge-success',
  paid: 'badge-success',
  yes: 'badge-success',
  inactive: 'badge-danger',
  rejected: 'badge-danger',
  overdue: 'badge-danger',
  pending: 'badge-warning',
  submitted: 'badge-warning',
  draft: 'badge-neutral',
  onboarding: 'badge-info',
};

export const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cls = BADGE_CLASS[s] || 'badge-neutral';
  return <span className={`badge ${cls}`}>{status || '—'}</span>;
};
